import PropTypes from 'prop-types'
import React from 'react'
import { Formik } from 'formik'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Envelope, Eye, EyeClosed, Lock } from '@phosphor-icons/react'

import { formValidation } from '../../FormLegacy/validation'
import { getPasswordInfo, renderPasswordCheckers } from '../../../helpers/helpers'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import { PASSWORD_SYMBOLS_AT_LEAST } from '../../../helpers/constants'
import { validatePasswordInfo } from '../utils'

const PasswordSetup = ({ formState, setFormState, setStep }) => {
  const { t } = useTranslation(['auth', 'common'])
  const validationSchema = Yup.object().shape({
    email: formValidation.email,
    password: formValidation.password,
    password2: formValidation.password2,
  })
  const [passwordValidation, setPasswordValidation] = React.useState({
    symbolsAtLeast: false,
    upperCase: false,
    lowerCase: false,
    oneNumber: false,
  })
  const [error, setError] = React.useState(null)
  const [showPassword, setShowPassword] = React.useState(false)

  const onSubmit = (values) => {
    if (!validatePasswordInfo(getPasswordInfo(values.password))) {
      setError(t('auth:resetPassword.checkRequirements'))
      return
    }

    setFormState((prev) => ({
      ...prev,
      password: values.password,
      email: values.email,
      password2: values.password2,
    }))
    setStep('user-data')
  }

  return (
    <Formik
      initialValues={{
        email: formState.email,
        password: formState.password,
        password2: formState.password2,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ handleSubmit, values, handleChange, touched, errors, setFieldTouched }) => (
        <form onSubmit={handleSubmit} className="auth__form">
          <button
            type="button"
            className="auth__back-button"
            onClick={() => setStep('community-code')}
          >
            <ArrowLeft size={20} />
            {t('common:actions.back')}
          </button>
          <p className="auth__step-description">{t('auth:passwordSetup.description')}</p>
          <TextField
            type="text"
            name="email"
            label={t('auth:shared.email')}
            placeholder={t('auth:shared.emailPlaceholder')}
            value={values.email}
            leftIcon={<Envelope />}
            onChange={handleChange}
            isError={touched.email && !!errors.email}
            errorLabel={errors.email}
            onBlur={() => setFieldTouched('email')}
          />
          <div className="password-wrapper">
            <TextField
              type={showPassword ? 'text' : 'password'}
              name="password"
              label={t('auth:shared.password')}
              placeholder={t('auth:shared.password')}
              value={values.password}
              leftIcon={<Lock />}
              onChange={(e) => {
                setPasswordValidation(getPasswordInfo(e.target.value))
                handleChange(e)
              }}
              isError={
                touched.password && (!!errors.password || !validatePasswordInfo(passwordValidation))
              }
              errorLabel={errors.password}
              onBlur={() => setFieldTouched('password')}
              rightIcon={
                <div className="password-eye" onClick={() => setShowPassword((prev) => !prev)}>
                  {showPassword ? <EyeClosed /> : <Eye />}
                </div>
              }
            />
          </div>
          <div className="password__checklist">
            <h3 className="password__title">{t('auth:passwordChecklist.title')}</h3>
            {renderPasswordCheckers([
              {
                name: t('auth:passwordChecklist.atLeastLetters', {
                  count: PASSWORD_SYMBOLS_AT_LEAST,
                }),
                value: passwordValidation.symbolsAtLeast,
              },
              { name: t('auth:passwordChecklist.lowerCase'), value: passwordValidation.lowerCase },
              { name: t('auth:passwordChecklist.upperCase'), value: passwordValidation.upperCase },
              { name: t('auth:passwordChecklist.number'), value: passwordValidation.oneNumber },
            ])}
          </div>
          <TextField
            type={showPassword ? 'text' : 'password'}
            name="password2"
            label={t('auth:resetPassword.confirmPasswordLabel')}
            placeholder={t('auth:shared.password')}
            value={values.password2}
            leftIcon={<Lock />}
            onChange={handleChange}
            isError={
              touched.password2 && (!!errors.password2 || !validatePasswordInfo(passwordValidation))
            }
            errorLabel={errors.password2}
            onBlur={() => setFieldTouched('password2')}
          />
          {error && <p className="auth__form__description error">{error}</p>}
          <button type="submit" className="button button-primary">
            {t('auth:shared.next')}
          </button>
          <div className="auth__form__existing-account">
            {t('auth:passwordSetup.existingAccount')}{' '}
            <Link className="link" to="/login">
              {t('auth:passwordSetup.signIn')}
            </Link>
          </div>
        </form>
      )}
    </Formik>
  )
}

PasswordSetup.propTypes = {
  formState: PropTypes.object.isRequired,
  setFormState: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
}

export default PasswordSetup
